import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { getValueAtDescriptorPath } from "../elementEditing";

function fieldByPath(fields, path) {
  return (fields || []).find((field) => field?.path === path) || null;
}

export default function FloatingWhatsappElementEditor({ descriptor, form, disabled, onCommit }) {
  const fields = descriptor?.fields || [];
  const visibleField = fieldByPath(fields, "sections.showFloatingWhatsapp");
  const numberField = fieldByPath(fields, "business.whatsapp");
  const messageField = fieldByPath(fields, "contact.whatsappMessage");
  const ariaField = fieldByPath(fields, "contact.floatingWhatsappAriaLabel");

  const visible = visibleField ? getValueAtDescriptorPath(form, visibleField.path, true) !== false : true;
  const number = String(getValueAtDescriptorPath(form, numberField?.path, "") || "");
  const message = String(getValueAtDescriptorPath(form, messageField?.path, "") || "");
  const ariaLabel = String(getValueAtDescriptorPath(form, ariaField?.path, "") || "");
  const configured = Boolean(number.trim());

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          p: 1.25,
          border: "1px solid rgba(25,36,47,.10)",
          borderRadius: 2.5,
          bgcolor: "rgba(25,36,47,.025)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Box
            aria-hidden="true"
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "#25D366",
              color: "#fff",
              boxShadow: "0 7px 18px rgba(0,0,0,.18)",
              opacity: visible ? 1 : 0.48,
            }}
          >
            <WhatsAppIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>WhatsApp flotante</Typography>
            <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" sx={{ mt: 0.55 }}>
              <Chip
                size="small"
                label={configured ? "WhatsApp configurado" : "Falta número"}
                variant={configured ? "filled" : "outlined"}
              />
              <Chip size="small" label={visible ? "Visible" : "Oculto"} variant="outlined" />
            </Stack>
          </Box>
        </Stack>
      </Box>

      {visibleField ? (
        <FormControlLabel
          control={
            <Switch
              checked={visible}
              disabled={disabled}
              onChange={(event) => onCommit?.(visibleField.path, event.target.checked)}
            />
          }
          label="Mostrar botón flotante"
        />
      ) : null}

      {numberField ? (
        <Stack spacing={0.6}>
          <Stack direction="row" spacing={0.7} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Número de WhatsApp</Typography>
            <Chip size="small" label="Dato compartido del negocio" variant="outlined" />
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Número"
            placeholder="Ej. +54 9 261 555 1234"
            value={number}
            disabled={disabled}
            inputProps={{ maxLength: numberField.maxLength }}
            onChange={(event) => onCommit?.(numberField.path, event.target.value)}
          />
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
            Se usa también en contacto, footer y otros accesos de WhatsApp.
          </Typography>
        </Stack>
      ) : null}

      {messageField ? (
        <Stack spacing={0.6}>
          <Stack direction="row" spacing={0.7} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Mensaje inicial</Typography>
            <Chip size="small" label="Compartido" variant="outlined" />
          </Stack>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Mensaje"
            value={message}
            disabled={disabled}
            inputProps={{ maxLength: messageField.maxLength }}
            onChange={(event) => onCommit?.(messageField.path, event.target.value)}
          />
        </Stack>
      ) : null}

      {ariaField ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon />}
            aria-controls="floating-whatsapp-accessibility-content"
            id="floating-whatsapp-accessibility-header"
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Accesibilidad</Typography>
              <Chip size="small" label="Opcional" variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails id="floating-whatsapp-accessibility-content">
            <TextField
              fullWidth
              size="small"
              label="Descripción para lectores de pantalla"
              value={ariaLabel}
              disabled={disabled}
              inputProps={{ maxLength: ariaField.maxLength }}
              onChange={(event) => onCommit?.(ariaField.path, event.target.value)}
            />
          </AccordionDetails>
        </Accordion>
      ) : null}

      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
        El botón conserva el color visual actual de WhatsApp. No se creó un color local que compita con la paleta del sitio.
      </Typography>
    </Stack>
  );
}
